import React, {createContext, useContext, useState, ReactNode} from 'react';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
    id: string;
    type: NotificationType;
    title?: string;
    message: string;
    duration?: number; // Auto-dismiss after X ms (0 = no auto-dismiss)
}

interface AdminNotificationContextType {
    notifications: Notification[];
    showNotification: (notification: Omit<Notification, 'id'>) => void;
    showSuccess: (message: string, title?: string) => void;
    showError: (message: string, title?: string) => void;
    showWarning: (message: string, title?: string) => void;
    showInfo: (message: string, title?: string) => void;
    dismissNotification: (id: string) => void;
    clearAllNotifications: () => void;
}

const AdminNotificationContext = createContext<AdminNotificationContextType | undefined>(undefined);

export const useAdminNotifications = (): AdminNotificationContextType => {
    const context = useContext(AdminNotificationContext);
    if (!context) {
        throw new Error('useAdminNotifications must be used within an AdminNotificationProvider');
    }
    return context;
};

interface AdminNotificationProviderProps {
    children: ReactNode;
}

export const AdminNotificationProvider: React.FC<AdminNotificationProviderProps> = ({children}) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);

    const generateId = () => `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const showNotification = (notification: Omit<Notification, 'id'>) => {
        const id = generateId();
        const newNotification: Notification = {
            id,
            duration: 5000, // Default 5 seconds
            ...notification,
        };

        setNotifications((prev) => [...prev, newNotification]);

        // Auto-dismiss if duration is set
        if (newNotification.duration && newNotification.duration > 0) {
            setTimeout(() => {
                dismissNotification(id);
            }, newNotification.duration);
        }
    };

    const showSuccess = (message: string, title?: string) => {
        showNotification({type: 'success', message, title});
    };

    const showError = (message: string, title?: string) => {
        showNotification({type: 'error', message, title, duration: 0}); // Errors don't auto-dismiss
    };

    const showWarning = (message: string, title?: string) => {
        showNotification({type: 'warning', message, title});
    };

    const showInfo = (message: string, title?: string) => {
        showNotification({type: 'info', message, title});
    };

    const dismissNotification = (id: string) => {
        setNotifications((prev) => prev.filter((notification) => notification.id !== id));
    };

    const clearAllNotifications = () => {
        setNotifications([]);
    };

    const value: AdminNotificationContextType = {
        notifications,
        showNotification,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        dismissNotification,
        clearAllNotifications,
    };

    return <AdminNotificationContext.Provider value={value}>{children}</AdminNotificationContext.Provider>;
};

export default AdminNotificationProvider;
